import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Search, Upload } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { dateShort, dateTime, money } from "@/lib/format";
import { useAuth } from "@/app/auth";
import { useLeads, useLeadActions, useIdentityHistory, useAssignableItems, useDisputes, personName, LEAD_STATUS_LABEL, LEAD_STATUS_VARIANT, LEAD_SOURCE_LABEL, type LeadRow, type LeadStatus } from "./api";
import { parseCsv, guessMapping, rowsToLeads, FIELDS, FIELD_LABEL, type Field } from "./csv";
import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { QueryError } from "@/components/shared/QueryError";
import { DeleteButton } from "@/components/shared/DeleteDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const DISPUTE_STATUS = { open: "Ouverte", resolved: "Close" } as const;

// ---------------------------------------------------------------------------
// Import CSV
// ---------------------------------------------------------------------------
function ImportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { importLeads } = useLeadActions();
  const [parsed, setParsed] = useState<{ headers: string[]; rows: string[][] } | null>(null);
  const [mapping, setMapping] = useState<Partial<Record<Field, number>>>({});
  const [result, setResult] = useState<{ imported: number; review: number; duplicates: number; errors: { row: number; error: string }[] } | null>(null);
  const [fileName, setFileName] = useState("");

  async function onFile(f: File) {
    const text = await f.text();
    const p = parseCsv(text);
    setFileName(f.name); setParsed(p); setMapping(guessMapping(p.headers)); setResult(null);
  }
  const ready = parsed && (mapping.phone !== undefined || mapping.email !== undefined);
  async function run() {
    if (!parsed) return;
    const rows = rowsToLeads(parsed.rows, mapping, parsed.headers).map((r) => ({ ...r, external_ref: `${fileName}:${r.external_ref}` }));
    const res = await importLeads.mutateAsync({ rows, source: "csv" });
    setResult(res);
  }
  function reset() { setParsed(null); setMapping({}); setResult(null); setFileName(""); onClose(); }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && reset()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader><DialogTitle>Importer des leads (CSV)</DialogTitle><DialogDescription>Une ligne par lead. Il faut au moins un téléphone ou un email. Les doublons exacts sont ignorés ; une personne déjà vendue pour le même produit part en revue — quel que soit le temps écoulé.</DialogDescription></DialogHeader>
        {!parsed ? (
          <div className="space-y-2">
            <Label htmlFor="csv-file">Fichier CSV</Label>
            <Input id="csv-file" type="file" accept=".csv,text/csv" onChange={(e) => e.target.files?.[0] && void onFile(e.target.files[0])} />
          </div>
        ) : result ? (
          <div className="space-y-3 text-sm">
            <p><strong>{result.imported}</strong> lead{result.imported > 1 ? "s" : ""} importé{result.imported > 1 ? "s" : ""}, <strong>{result.review}</strong> à revoir (doublon probable), <strong>{result.duplicates}</strong> ignoré{result.duplicates > 1 ? "s" : ""} (déjà importé{result.duplicates > 1 ? "s" : ""}), <strong>{result.errors.length}</strong> en erreur.</p>
            {result.errors.length > 0 && <ul className="max-h-48 overflow-auto rounded-xl bg-muted p-3 text-xs">{result.errors.map((e) => <li key={e.row}>Ligne {e.row} : {e.error}</li>)}</ul>}
            <div className="flex justify-end"><Button onClick={reset}>Fermer</Button></div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{fileName} — {parsed.rows.length} ligne{parsed.rows.length > 1 ? "s" : ""}. Vérifie la correspondance des colonnes :</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {FIELDS.map((f) => (
                <div key={f} className="space-y-1">
                  <Label htmlFor={`map-${f}`}>{FIELD_LABEL[f]}{(f === "phone" || f === "email") && " *"}</Label>
                  <Select value={mapping[f] === undefined ? "__none" : String(mapping[f])} onValueChange={(v) => setMapping({ ...mapping, [f]: v === "__none" ? undefined : Number(v) })}>
                    <SelectTrigger id={`map-${f}`} className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="__none">— non importé —</SelectItem>{parsed.headers.map((h, i) => <SelectItem key={i} value={String(i)}>{h || `colonne ${i + 1}`}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">Les colonnes non mappées sont conservées dans le contenu du lead. * téléphone ou email requis.</p>
            <div className="max-h-40 overflow-auto rounded-xl border border-border text-xs">
              <table className="w-full"><thead className="bg-muted/60"><tr>{parsed.headers.map((h, i) => <th key={i} className="px-2 py-1 text-left font-medium">{h}</th>)}</tr></thead>
                <tbody>{parsed.rows.slice(0, 5).map((r, i) => <tr key={i} className="border-t border-border/60">{r.map((c, j) => <td key={j} className="max-w-[160px] truncate px-2 py-1">{c}</td>)}</tr>)}</tbody></table>
            </div>
            <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setParsed(null)}>Autre fichier</Button><Button disabled={!ready || importLeads.isPending} onClick={() => void run()}><Upload className="mr-2 h-4 w-4" />Importer {parsed.rows.length} lead{parsed.rows.length > 1 ? "s" : ""}</Button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Saisie manuelle
// ---------------------------------------------------------------------------
function AddLeadDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { importLeads } = useLeadActions();
  const { data: products } = useQuery({ queryKey: ["products", "active"], queryFn: async () => { const { data, error } = await supabase.from("products").select("id, sku, name").eq("is_active", true).order("name"); if (error) throw error; return data; } });
  const [v, setV] = useState({ first_name: "", last_name: "", phone: "", email: "", sku: "", zone: "", notes: "" });
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof v) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setV({ ...v, [k]: e.target.value });
  async function submit() {
    if (!v.phone.trim() && !v.email.trim()) { setError("Téléphone ou email requis."); return; }
    const res = await importLeads.mutateAsync({ rows: [{ ...v, external_ref: null, received_at: null, payload: v.notes ? { notes: v.notes } : {} }], source: "manual" });
    if (res.errors.length) { setError(res.errors[0]!.error); return; }
    setV({ first_name: "", last_name: "", phone: "", email: "", sku: "", zone: "", notes: "" }); setError(null); onClose();
  }
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Nouveau lead</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1"><Label htmlFor="l-first">Prénom</Label><Input id="l-first" value={v.first_name} onChange={set("first_name")} /></div>
          <div className="space-y-1"><Label htmlFor="l-last">Nom</Label><Input id="l-last" value={v.last_name} onChange={set("last_name")} /></div>
          <div className="space-y-1"><Label htmlFor="l-phone">Téléphone *</Label><Input id="l-phone" value={v.phone} onChange={set("phone")} placeholder="06 12 34 56 78" /></div>
          <div className="space-y-1"><Label htmlFor="l-email">Email *</Label><Input id="l-email" type="email" value={v.email} onChange={set("email")} /></div>
          <div className="space-y-1">
            <Label htmlFor="l-sku">Produit</Label>
            <Select value={v.sku} onValueChange={(s) => setV({ ...v, sku: s })}><SelectTrigger id="l-sku"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{products?.map((p) => <SelectItem key={p.id} value={p.sku}>{p.sku} — {p.name}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="space-y-1"><Label htmlFor="l-zone">Zone / ville</Label><Input id="l-zone" value={v.zone} onChange={set("zone")} /></div>
          <div className="space-y-1 sm:col-span-2"><Label htmlFor="l-notes">Besoin / notes</Label><Textarea id="l-notes" rows={3} value={v.notes} onChange={set("notes")} /></div>
        </div>
        <p className="text-xs text-muted-foreground">* au moins l'un des deux</p>
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end"><Button onClick={() => void submit()} disabled={importLeads.isPending}>Ajouter le lead</Button></div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Fiche lead (panneau latéral) : identité, historique, attribution, revue
// ---------------------------------------------------------------------------
export function LeadDrawer({ lead, onClose }: { lead: LeadRow | null; onClose: () => void }) {
  const { roles } = useAuth();
  const { data: history } = useIdentityHistory(lead?.identity_id);
  const { data: items } = useAssignableItems(lead?.product_id, lead?.id);
  const { assign, review, anonymize } = useLeadActions();
  const [itemId, setItemId] = useState("");
  const [note, setNote] = useState("");
  const [anonReason, setAnonReason] = useState("");
  const [anonOpen, setAnonOpen] = useState(false);
  const [releasing, setReleasing] = useState(false);
  if (!lead) return null;
  const idn = lead.lead_identities;
  const payload = (lead.payload as Record<string, unknown>) ?? {};
  const others = (history ?? []).filter((h) => h.lead_id !== lead.id);
  const lockedName = lead.locked_client_id ? others.find((h) => h.client_name)?.client_name ?? null : null;
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">{personName(idn)} <Badge variant={LEAD_STATUS_VARIANT[lead.status]}>{LEAD_STATUS_LABEL[lead.status]}</Badge></SheetTitle>
          <SheetDescription>{lead.products ? `${lead.products.sku} — ${lead.products.name}` : "Produit non défini"}{lead.zone ? ` · ${lead.zone}` : ""} · reçu le {dateTime(lead.received_at)} · {LEAD_SOURCE_LABEL[lead.source]}</SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-5 text-sm">
          <section className="glass rounded-2xl p-4">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Identité</h3>
            {idn?.anonymized_at ? <p className="text-muted-foreground">Données personnelles effacées le {dateShort(idn.anonymized_at)}. L'empreinte d'exclusivité est conservée.</p> : (
              <dl className="space-y-1">
                <div className="flex justify-between"><dt className="text-muted-foreground">Téléphone</dt><dd>{idn?.phone ?? "—"}</dd></div>
                <div className="flex justify-between"><dt className="text-muted-foreground">Email</dt><dd>{idn?.email ?? "—"}</dd></div>
              </dl>
            )}
            {Object.keys(payload).length > 0 && !payload.anonymized && (
              <dl className="mt-3 space-y-1 border-t border-border/60 pt-2">{Object.entries(payload).map(([k, val]) => <div key={k} className="flex justify-between gap-3"><dt className="text-muted-foreground">{k}</dt><dd className="text-right">{String(val)}</dd></div>)}</dl>
            )}
          </section>

          {lead.review_status === "pending" && (
            <section className="rounded-2xl border border-destructive/30 bg-destructive/10 p-4">
              <h3 className="mb-1 font-semibold text-destructive">Déjà vendu — exclusivité</h3>
              <p className="mb-3 text-muted-foreground">Cette personne a déjà été vendue pour ce produit{lockedName ? <> à <strong>{lockedName}</strong></> : ""} (voir l'historique). Elle lui reste réservée, quel que soit le temps écoulé.</p>
              <Textarea rows={2} placeholder={releasing ? "Motif obligatoire pour libérer" : "Note (facultative)"} value={note} onChange={(e) => setNote(e.target.value)} />
              {!releasing ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => review.mutate({ leadId: lead.id, decision: "same_client", note }, { onSuccess: onClose })}>Nouvelle demande, même client</Button>
                  <Button size="sm" variant="outline" onClick={() => review.mutate({ leadId: lead.id, decision: "merged", note }, { onSuccess: onClose })}>Même demande — rejeter</Button>
                  {roles.includes("admin") && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setReleasing(true)}>Libérer pour un autre client…</Button>}
                </div>
              ) : (
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button size="sm" variant="destructive" disabled={!note.trim()} onClick={() => review.mutate({ leadId: lead.id, decision: "release", note }, { onSuccess: onClose })}>Libérer (tracé)</Button>
                  <Button size="sm" variant="ghost" onClick={() => setReleasing(false)}>Annuler</Button>
                </div>
              )}
            </section>
          )}
          {lead.status === "received" && lead.locked_client_id && lead.review_status !== "released" && (
            <p className="rounded-2xl bg-muted px-4 py-2 text-xs text-muted-foreground">Exclusivité : attribuable uniquement à {lockedName ?? "son client d'origine"}.</p>
          )}
          {lead.review_status === "released" && <p className="rounded-2xl bg-muted px-4 py-2 text-xs text-muted-foreground">Libéré de son exclusivité par un admin : {lead.review_note}</p>}

          {lead.status === "received" && (
            <section className="glass rounded-2xl p-4">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Attribuer à une commande</h3>
              {!items ? <p className="text-muted-foreground">Recherche des commandes…</p> : items.length === 0 ? <p className="text-muted-foreground">Aucune commande confirmée n'attend ce produit{lead.locked_client_id && lead.review_status !== "released" ? " pour son client d'origine" : ""}.</p> : (
                <div className="space-y-2">
                  <Select value={itemId} onValueChange={setItemId}>
                    <SelectTrigger aria-label="Ligne de commande"><SelectValue placeholder="Choisir une commande" /></SelectTrigger>
                    <SelectContent>{items?.map((it) => <SelectItem key={it.order_item_id} value={it.order_item_id}>{it.order_number} · {it.client_name} · {it.label} — reste {Number(it.remaining)} / {Number(it.quantity)}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="sm" disabled={!itemId || assign.isPending} onClick={() => assign.mutate({ leadId: lead.id, orderItemId: itemId }, { onSuccess: onClose })}>Attribuer</Button>
                </div>
              )}
            </section>
          )}

          {lead.clients && <section className="glass rounded-2xl p-4"><h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Vendu à</h3><p className="font-medium">{lead.clients.legal_name}</p></section>}

          <section className="glass rounded-2xl p-4">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Historique de cette personne</h3>
            {others.length === 0 ? <p className="text-muted-foreground">Aucune autre demande.</p> : (
              <ul className="space-y-1">{others.map((h) => <li key={h.lead_id} className="flex justify-between gap-2"><span>{dateShort(h.received_at)} · {h.product_name ?? "—"}{h.zone ? ` · ${h.zone}` : ""}</span><span className="text-muted-foreground">{LEAD_STATUS_LABEL[h.status]}{h.client_name ? ` → ${h.client_name}` : ""}</span></li>)}</ul>
            )}
          </section>

          {roles.includes("admin") && (
            <section className="rounded-2xl border border-border p-4">
              <DeleteButton entity="lead" id={lead.id} label="Supprimer ce lead" onDeleted={onClose} onArchived={onClose} />
            </section>
          )}

          {roles.includes("admin") && idn && !idn.anonymized_at && (
            <section className="rounded-2xl border border-border p-4">
              {!anonOpen ? <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setAnonOpen(true)}>Anonymiser cette personne (RGPD)</Button> : (
                <div className="space-y-2">
                  <p className="text-muted-foreground">Efface nom, téléphone, email et contenu de toutes ses demandes. Irréversible. L'historique de vente et l'exclusivité restent.</p>
                  <Input placeholder="Motif *" value={anonReason} onChange={(e) => setAnonReason(e.target.value)} />
                  <div className="flex gap-2"><Button size="sm" variant="destructive" disabled={!anonReason.trim()} onClick={() => anonymize.mutate({ identityId: idn.id, reason: anonReason }, { onSuccess: onClose })}>Anonymiser</Button><Button size="sm" variant="ghost" onClick={() => setAnonOpen(false)}>Annuler</Button></div>
                </div>
              )}
            </section>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export function LeadsTable({ leads, onOpen }: { leads: LeadRow[]; onOpen: (l: LeadRow) => void }) {
  return (
    <div className="glass overflow-hidden rounded-2xl">
      <Table>
        <TableHeader><TableRow><TableHead>Reçu</TableHead><TableHead>Personne</TableHead><TableHead>Contact</TableHead><TableHead>Produit</TableHead><TableHead>Zone</TableHead><TableHead>Client</TableHead><TableHead>Statut</TableHead></TableRow></TableHeader>
        <TableBody>
          {leads.map((l) => (
            <TableRow key={l.id} className="cursor-pointer" onClick={() => onOpen(l)}>
              <TableCell className="whitespace-nowrap">{dateShort(l.received_at)}</TableCell>
              <TableCell className="font-medium">{personName(l.lead_identities)}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{l.lead_identities?.anonymized_at ? "—" : [l.lead_identities?.phone, l.lead_identities?.email].filter(Boolean).join(" · ")}</TableCell>
              <TableCell>{l.products?.name ?? "—"}</TableCell>
              <TableCell>{l.zone ?? "—"}</TableCell>
              <TableCell>{l.clients?.legal_name ?? "—"}</TableCell>
              <TableCell><Badge variant={LEAD_STATUS_VARIANT[l.status]}>{LEAD_STATUS_LABEL[l.status]}</Badge></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function LeadsPage() {
  const nav = useNavigate();
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("all");
  const [importing, setImporting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<LeadRow | null>(null);
  const all = useLeads({ status, search });
  const review = useLeads({ review: true });
  const disputes = useDisputes();
  const current = useMemo(() => (open ? (all.data ?? []).concat(review.data ?? []).find((l) => l.id === open.id) ?? open : null), [open, all.data, review.data]);

  return (
    <div>
      <PageHeader title="Leads" description="Une personne vendue pour un produit reste réservée à ce client, pour toujours. Toute nouvelle demande identique passe en revue."
        actions={<><Button variant="outline" onClick={() => setImporting(true)}><Upload className="mr-2 h-4 w-4" />Importer un CSV</Button><Button onClick={() => setAdding(true)}><Plus className="mr-2 h-4 w-4" />Nouveau lead</Button></>} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 h-auto flex-wrap justify-start gap-1">
          <TabsTrigger value="all">Tous</TabsTrigger>
          <TabsTrigger value="review">À revoir{review.data && review.data.length > 0 ? ` (${review.data.length})` : ""}</TabsTrigger>
          <TabsTrigger value="disputes">Contestations{disputes.data ? ` (${disputes.data.filter((d) => d.status === "open").length})` : ""}</TabsTrigger>
        </TabsList>
        <TabsContent value="all">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" /><Input className="w-72 pl-8" placeholder="Nom, téléphone, email, zone…" value={search} onChange={(e) => setSearch(e.target.value)} /></div>
            <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-44"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Tous les statuts</SelectItem>{(Object.keys(LEAD_STATUS_LABEL) as LeadStatus[]).map((k) => <SelectItem key={k} value={k}>{LEAD_STATUS_LABEL[k]}</SelectItem>)}</SelectContent></Select>
            {all.data && <span className="ml-auto text-sm text-muted-foreground">{all.data.length} lead{all.data.length > 1 ? "s" : ""}</span>}
          </div>
          {all.error ? <QueryError error={all.error} /> : all.isLoading ? <p className="text-sm text-muted-foreground">Chargement…</p> : !all.data?.length ? <EmptyState title="Aucun lead" description="Importe un CSV ou saisis un lead. Ils seront ensuite attribués aux commandes confirmées." /> : <LeadsTable leads={all.data} onOpen={setOpen} />}
        </TabsContent>
        <TabsContent value="review">
          {review.data && review.data.length === 0 ? <EmptyState title="Rien à revoir" description="Apparaîtront ici les demandes de personnes déjà vendues pour le même produit, quel que soit le temps écoulé." /> : review.data && <LeadsTable leads={review.data} onOpen={setOpen} />}
        </TabsContent>
        <TabsContent value="disputes">
          {disputes.data && disputes.data.length === 0 ? <EmptyState title="Aucune contestation" description="Une contestation s'ouvre depuis une commande, sur des leads livrés." /> : (
            <div className="glass overflow-hidden rounded-2xl">
              <Table>
                <TableHeader><TableRow><TableHead>N°</TableHead><TableHead>Client</TableHead><TableHead>Commande</TableHead><TableHead>Ouverte le</TableHead><TableHead>Décisions</TableHead><TableHead className="text-right">Avoir</TableHead><TableHead>Statut</TableHead></TableRow></TableHeader>
                <TableBody>{disputes.data?.map((d) => (
                  <TableRow key={d.id} className="cursor-pointer" onClick={() => nav(`/contestations/${d.id}`)}>
                    <TableCell className="font-mono text-xs">{d.number}</TableCell><TableCell className="font-medium">{d.clients?.legal_name}</TableCell><TableCell className="font-mono text-xs">{d.orders?.number}</TableCell><TableCell>{dateShort(d.opened_at)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{d.status === "resolved" ? `${d.replaced_count} remplacé · ${d.credited_count} crédité · ${d.rejected_count} rejeté` : "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(d.credit_amount, d.currency)}</TableCell><TableCell><Badge variant={d.status === "open" ? "destructive" : "secondary"}>{DISPUTE_STATUS[d.status]}</Badge></TableCell>
                  </TableRow>))}</TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>
      <ImportDialog open={importing} onClose={() => setImporting(false)} />
      <AddLeadDialog open={adding} onClose={() => setAdding(false)} />
      <LeadDrawer lead={current} onClose={() => setOpen(null)} />
    </div>
  );
}
